import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, History, LogOut } from "lucide-react";
import { BRAND } from "@/lib/brand";
import PunchTicker from "@/components/PunchTicker";

/**
 * Mobile-first shell with two proper app bars.
 *
 * Top app bar   -> respects safe-area-inset-top (Android status bar / iOS notch)
 * Bottom app bar-> respects safe-area-inset-bottom (Android gesture pill / iOS home indicator)
 *
 * Both bars are `fixed` so the middle scrolls independently — feels native inside
 * the Capacitor wrapper and still works in a normal browser.
 */
export default function EmployeeLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ------------------------- TOP APP BAR ------------------------- */}
      <header
        data-testid="employee-top-bar"
        className="fixed top-0 inset-x-0 z-40 border-b border-border bg-background/95 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-md md:max-w-3xl mx-auto h-14 px-4 flex items-center justify-between">
          <div className="min-w-0 flex items-center gap-3">
            <div className="h-8 w-8 grid place-items-center bg-[hsl(var(--primary))] text-white font-display font-black text-sm">
              BT
            </div>
            <div className="min-w-0">
              <div className="label-mono leading-none">{BRAND.subtitle}</div>
              <div
                data-testid="employee-name"
                className="font-display text-sm leading-tight truncate max-w-[45vw]"
              >
                {user?.name}
              </div>
            </div>
          </div>
          <button
            data-testid="employee-logout-btn"
            onClick={logout}
            className="h-9 w-9 grid place-items-center border border-border hover:bg-secondary shrink-0"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* ------------------------- CONTENT ------------------------- */}
      {/* Reserve room for top + bottom bars via CSS variables the child pages inherit */}
      <main
        className="max-w-md md:max-w-3xl mx-auto px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 3.5rem + 0.75rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 6rem + 1rem)",
        }}
      >
        <Outlet />
      </main>

      {/* ------------------------- BOTTOM APP BAR ------------------------- */}
      <nav
        data-testid="employee-bottom-bar"
        className="fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <PunchTicker />
        <div className="max-w-md md:max-w-3xl mx-auto grid grid-cols-2 h-16">
          {[
            { to: "/employee", icon: Home, label: "Today", end: true, testid: "nav-today" },
            { to: "/employee/history", icon: History, label: "Timeline", testid: "nav-history" },
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 text-xs transition-colors ${
                  isActive
                    ? "text-[hsl(var(--accent))]"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <n.icon
                    className="h-5 w-5"
                    strokeWidth={isActive ? 2.25 : 1.5}
                  />
                  <span className="font-mono-brand tracking-wide">{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
